import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

const days = [
  { label: 'M', value: 1, fullName: 'Monday' },
  { label: 'T', value: 2, fullName: 'Tuesday' },
  { label: 'W', value: 3, fullName: 'Wednesday' },
  { label: 'Th', value: 4, fullName: 'Thursday' },
  { label: 'F', value: 5, fullName: 'Friday' },
  { label: 'S', value: 6, fullName: 'Saturday' },
  { label: 'Su', value: 7, fullName: 'Sunday' },
];

interface WorkWeekConfigFormProps {
  weekStart: number | null | undefined;
  onWeekStartChange: (value: number) => void;
  isLoading?: boolean;
  error?: React.ReactNode;
  isCustomized: boolean;
  onRevert: () => void;
  integrated?: boolean;
}

export const WorkWeekConfigForm: React.FC<WorkWeekConfigFormProps> = ({
  weekStart,
  onWeekStartChange,
  isLoading,
  error,
  isCustomized,
  onRevert,
  integrated = false,
}) => {
  const getSelectedDayName = (dayValue: number | null | undefined) => {
    if (dayValue === null || dayValue === undefined) return 'Not selected';
    return days.find(d => d.value === dayValue)?.fullName ?? 'Invalid day';
  };

  if (integrated) {
    return (
      <div className="space-y-1.5">
        <div
          data-testid="work-week-start-field"
          className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
        >
          <div className="min-w-0 leading-none sm:flex-1">
            <div className="flex items-center justify-between gap-2 sm:block">
              <span
                data-testid="work-week-start-label"
                className={cn(fieldLabelClass, "align-middle")}
              >
                Work Week Start
              </span>
              {weekStart != null && (
                <span
                  data-testid="work-week-start-badge"
                  className="inline-flex items-center align-middle rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-0.5 font-heading text-[12px] font-semibold uppercase tracking-wide text-primary-text sm:ml-2"
                >
                  {getSelectedDayName(weekStart)}
                </span>
              )}
            </div>
            <p className={cn(fieldDescriptionClass, "hidden sm:block sm:mt-2 leading-snug")}>
              Which day starts the 7-day window for weekly overtime.
            </p>
          </div>
          <div id="week-start-picker" className="flex justify-between space-x-1 mt-1 sm:mt-0 sm:justify-end sm:flex-shrink-0" role="group">
            {days.map((day) => (
              <Button
                key={day.value}
                variant={weekStart === day.value ? undefined : 'outline'}
                size="icon-xs"
                className={cn(
                  "h-9 w-9 min-w-9 flex-none px-0 text-[12px] touch-target-pseudo sm:h-11 sm:w-auto sm:min-w-[44px] sm:flex-1 sm:text-sm",
                  weekStart === day.value && "bg-primary text-[var(--color-surface-0)] font-semibold hover:bg-primary/90"
                )}
                onClick={() => onWeekStartChange(day.value)}
                disabled={isLoading}
                aria-label={`Set week start to ${day.fullName}`}
              >
                {day.label}
              </Button>
            ))}
          </div>
          <p className={cn(fieldDescriptionClass, "sm:hidden")}>
            Which day starts the 7-day window for weekly overtime.
          </p>
        </div>
        {error && <div className="text-xs text-destructive">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4 mt-4 border-t">
      <div className="space-y-1 text-center">
          <h4 className="text-base font-medium text-foreground">Work Week</h4>
          <p className="text-sm text-muted-foreground px-2 max-w-md mx-auto">
            Which day starts the 7-day window for weekly overtime.
          </p>
      </div>

      <div className="space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Label htmlFor="week-start-picker" className={fieldLabelClass}>
              Work week starts on: <span className="font-normal text-muted-foreground">{getSelectedDayName(weekStart)}</span>
            </Label>
          </div>
          <div id="week-start-picker" className="flex justify-center space-x-1 pt-1" role="group">
              {days.map((day) => (
                <Button
                  key={day.value}
                  variant={weekStart === day.value ? undefined : 'outline'}
                  size="icon-xs"
                  className={cn(
                    "flex-1 max-w-12 h-11 min-w-[44px] md:h-12",
                    weekStart === day.value && "bg-primary text-[var(--color-surface-0)] font-semibold hover:bg-primary/90"
                  )}
                  onClick={() => onWeekStartChange(day.value)}
                  disabled={isLoading}
                  aria-label={`Set week start to ${day.fullName}`}
                >
                  {day.label}
                </Button>
              ))}
          </div>
          <div className="h-4 text-right">
              {error && <div className="text-xs text-destructive mt-1">{error}</div>}
          </div>
      </div>
      {isCustomized && onRevert && (
        <div className="flex justify-center">
          <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
            Revert to Profile Defaults
          </Button>
        </div>
      )}
    </div>
  );
};
